import React, { forwardRef, useImperativeHandle } from 'react';
import {
  Link, styled, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { FormikHelpers, FormikProvider, useFormik } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';

import { GetCustomerLoginFormSchema, GetCustomerLoginFormInitialValue, ICustomerLoginFormFields } from '../../schemas';
import { FormField } from '../shared';
import { useEnterToSubmit } from '../../hooks';

const LoginTextField = styled(FormField<ICustomerLoginFormFields>)(({ theme }) => ({
  width: '100%',
  marginTop: '15px',
}));

const LoginForm = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 15,
}));

const styleProp = {
  background: '#FFFEFE 0% 0% no-repeat padding-box',
  border: '1px solid #0000001F',
};

const inputStyleAccNo = {
  style: styleProp,
  maxLength: 10,
};

const inputStyleSSN = {
  style: styleProp,
  maxLength: 4,
};

const CustomerLoginForm = forwardRef<ICustomerLoginForm, ICustomerLoginFormProps>((props, ref) => {
  const { onSubmit, onSampleBillLinkClick } = props;

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const intl = useIntl();
  const formik = useFormik({
    initialValues: GetCustomerLoginFormInitialValue(),
    validationSchema: GetCustomerLoginFormSchema(intl),
    onSubmit,
    initialTouched: {
      accountNumber: true,
      ssn4: true,
    },
  });
  const enterToSubmit = useEnterToSubmit(formik);

  useImperativeHandle(
    ref,
    () => ({
      handleSubmit: formik.handleSubmit,
    }),
    [formik.handleSubmit],
  );

  return (
    <FormikProvider value={formik}>
      <LoginForm data-testid="form" onKeyDown={enterToSubmit}>
        <LoginTextField
          formProps={formik}
          textFieldProps={{
            variant: 'filled',
            name: 'accountNumber',
            inputProps: inputStyleAccNo,
            InputLabelProps: {
              style: { color: theme.palette.primary.dark },
            },
          }}
        />
        {isSmallScreen && (
          <Link
            underline="hover"
            onClick={onSampleBillLinkClick}
            sx={{ cursor: 'default' }}
          >
            <Typography variant="body2">
              <FormattedMessage id="customer-login.sample-bill-link" />
            </Typography>
          </Link>
        )}
        <LoginTextField
          formProps={formik}
          textFieldProps={{
            variant: 'filled',
            name: 'ssn4',
            type: 'password',
            inputProps: inputStyleSSN,
            InputLabelProps: {
              style: { color: theme.palette.primary.dark },
            },
          }}
        />
      </LoginForm>
    </FormikProvider>
  );
});

interface ICustomerLoginForm {
  handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void;
}

interface ICustomerLoginFormProps {
  onSubmit: (
    values: ICustomerLoginFormFields,
    formikHelpers: FormikHelpers<ICustomerLoginFormFields>
  ) => void | Promise<any>;
  onSampleBillLinkClick: () => void;
}

export type { ICustomerLoginForm, ICustomerLoginFormProps };
export { CustomerLoginForm };
